import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Header() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      const sections = ['home', 'about', 'approach', 'testimonials', 'services-pricing', 'education', 'contact'];
      const headerHeight = 80; // Account for sticky header height
      
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= headerHeight + 50 && rect.bottom >= headerHeight + 50;
        }
        return false;
      });
      
      if (current) {
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const goHome = () => {
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const menuItems = [
    { id: 'about', label: 'About' },
    { id: 'approach', label: 'Art Therapy' },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'services-pricing', label: 'Services & Pricing' },
    { id: 'education', label: 'Education & Certification' },
    { id: 'blog', label: 'Blog', href: 'https://blog.yuvati.art' },
    { id: 'contact', label: 'Contact' }
  ];

  const handleBookSession = () => {
    scrollToSection('services-pricing');
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#829BB8] shadow-xl border-b border-[#3D5C4A]' 
          : 'bg-[#829BB8]'
      }`}>
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between py-2 md:py-4 h-16 md:h-20">
            {/* Logo/Brand */}
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <button
                onClick={goHome}
                className="flex-shrink-0 w-10 h-12 md:w-10 md:h-12 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D6794E]"
                aria-label="Go to home"
              >
                <img
                  src="https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/48064541-945d-4db4-9b5f-090b501b738b_compressed_Logo-Symbol-White.webp"
                  alt="Yuvati Art logo"
                  className="w-full h-full object-contain"
                />
              </button>
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-base md:text-2xl lg:text-3xl font-normal text-[#FBF7F0] leading-tight cursor-pointer whitespace-nowrap"
                    onClick={goHome}>
                  Yuliya Yuvati
                </h1>
                <p className="text-[11px] md:text-sm text-[#FBF7F0]/80 leading-tight whitespace-nowrap">
                  Art Therapist & Counselor
                </p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {menuItems.map((item) => (
                item.href ? (
                  <a
                    key={item.id}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative text-sm font-normal transition-all duration-200 cursor-pointer whitespace-nowrap px-3 py-2 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D6794E] text-[#FBF7F0]/90 hover:text-white hover:bg-[#D6794E]"
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`relative text-sm font-normal transition-all duration-200 cursor-pointer whitespace-nowrap px-3 py-2 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D6794E] ${
                      activeSection === item.id
                        ? 'text-white bg-[#D6794E]'
                        : 'text-[#FBF7F0]/90 hover:text-white hover:bg-[#D6794E]'
                    }`}
                  >
                    {item.label}
                    {activeSection === item.id && (
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#D6794E] rounded-full"></div>
                    )}
                  </button>
                )
              ))}
              
              {/* Book Session Button */}
              <button
                onClick={handleBookSession}
                className="bg-[#D6794E] hover:bg-[#385B47] text-white px-6 py-2.5 rounded-xl font-normal text-sm transition-all duration-200 cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D6794E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#829BB8] shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Book Session
              </button>
            </nav>
            
            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 text-[#FBF7F0] hover:text-[#D6794E] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D6794E] rounded-lg cursor-pointer"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl transition-transform duration-200 ${
                isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'
              }`}></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
        isMobileMenuOpen ? 'visible' : 'invisible'
      }`}>
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-[#829BB8]/60 backdrop-blur-sm transition-opacity duration-300 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Drawer */}
        <div className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-[#829BB8] border-l border-[#3D5C4A] shadow-2xl transform transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#3D5C4A]">
              <div className="flex flex-col">
                <h2 className="text-xl font-normal text-[#FBF7F0]">
                  Yuliya
                </h2>
                <p className="text-sm text-[#FBF7F0]/80">
                  Art Therapist
                </p>
              </div>
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-[#FBF7F0] hover:text-[#D6794E] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D6794E] rounded-lg cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            {/* Navigation */}
            <nav className="flex-1 px-6 py-8">
              <div className="space-y-2">
                {menuItems.map((item) => (
                  item.href ? (
                    <a
                      key={item.id}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-left px-4 py-3 rounded-xl transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D6794E] text-[#FBF7F0]/90 hover:text-white hover:bg-[#D6794E] block"
                    >
                      <span className="font-normal">{item.label}</span>
                    </a>
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D6794E] ${
                        activeSection === item.id
                          ? 'text-white bg-[#D6794E] border-l-4 border-[#D6794E]'
                          : 'text-[#FBF7F0]/90 hover:text-white hover:bg-[#D6794E]'
                      }`}
                    >
                      <span className="font-normal">{item.label}</span>
                    </button>
                  )
                ))}
              </div>
            </nav>
            
            {/* Book Session Button */}
            <div className="p-6 border-t border-[#3D5C4A]">
              <button
                onClick={handleBookSession}
                className="w-full bg-[#D6794E] hover:bg-[#385B47] text-white px-6 py-3 rounded-xl font-normal transition-all duration-200 cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D6794E] shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Book Session
                <i className="ri-calendar-line ml-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}